// pairing.js - QR 스캔 & 페어링 관리

import { uploadQueue } from './upload-queue.js';
import { t } from './i18n/index.js';
import { setSentryUser, clearSentryUser } from './sentry.js';
import { notifySessionChanged } from './utils/tab-manager.js';
import { getPhoneToken } from './lib/phone-token.js';

const STORAGE_KEY = 'scantodesk_pairing';
const PHONE_ID_KEY = 'scantodesk_phone_id';
const PERSISTENT_PAIRINGS_KEY = 'scantodesk_persistent_pairings';
const API_BASE = import.meta.env.VITE_API_BASE || 'https://api.scantodesk.app';

let qrScanner = null;

/**
 * Generate a UUID v4 with fallback for non-secure contexts
 * crypto.randomUUID() requires HTTPS, so we need a fallback for local dev (HTTP)
 */
function generateUUID() {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback using crypto.getRandomValues() which works in non-secure contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or generate a unique phone identifier
 * Delegates to getPhoneToken() for triple-storage persistence
 * @returns {Promise<string>} Phone identifier (UUID v4)
 */
export async function getPhoneIdentifier() {
  return getPhoneToken();
}

/**
 * Get a friendly name for this phone
 * Tries to extract from user agent or uses default
 */
export function getPhoneName() {
  const ua = navigator.userAgent;

  // Try to extract device name from user agent
  if (/iPhone/.test(ua)) {
    return 'iPhone';
  } else if (/iPad/.test(ua)) {
    return 'iPad';
  } else if (/Android/.test(ua)) {
    // Try to get Android device name
    const match = ua.match(/;\s*([^;)]+)\s*Build/);
    if (match && match[1]) {
      return match[1].trim();
    }
    return 'Android';
  }

  return 'Mobile';
}

/**
 * Extract desktop name from URL parameters
 */
export function extractDesktopName(url) {
  try {
    const urlObj = new URL(url);
    const name = urlObj.searchParams.get('name');
    return name ? decodeURIComponent(name) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Short code URL에서 페어링 정보 추출 (신규 방식)
 * URL 형식: /s/{7자리코드}?name={desktopName} 또는 go.scantodesk.app/s/{코드}?name={desktopName}
 * @returns {Promise<Object|null>} 페어링 정보 또는 null
 */
export async function handleShortCodeLink() {
  try {
    const path = window.location.pathname;
    const codeMatch = path.match(/^\/s\/([A-HJ-NP-Z2-9]{7})$/i);

    if (!codeMatch) return null;

    const code = codeMatch[1].toUpperCase();
    console.log('Short code detected:', code);

    // Extract desktop name from URL parameter
    const desktopName = extractDesktopName(window.location.href);
    if (desktopName) {
      console.log('Desktop name from QR:', desktopName);
    }

    // API 호출하여 세션 정보 조회
    const response = await fetch(`${API_BASE}/s/${code}`);
    const data = await response.json();

    if (!data.valid) {
      console.error('Short code invalid:', data.error);
      // 에러 타입에 따른 사용자 친화적 메시지
      if (data.error === 'code_expired') {
        throw new Error(t('pairing.error.codeExpired'));
      } else if (data.error === 'code_not_found') {
        throw new Error(t('pairing.error.codeNotFound'));
      }
      throw new Error(t('pairing.error.connectionFailed'));
    }

    // Confirm before overwriting if pending uploads exist
    if (!confirmPendingUploads()) {
      return null;
    }

    // 페어링 정보 저장 (desktop name 포함)
    const pairingInfo = {
      device_id: data.sessionId,
      endpoint: data.endpoint,
      credentials: { apiKey: data.apiKey },
      desktop_name: desktopName || data.deviceName || null,
      desktop_fingerprint: data.fingerprint || null,
      paired_at: new Date().toISOString()
    };

    if (!savePairingInfo(pairingInfo)) {
      console.error('Failed to save pairing info from short code');
      throw new Error(t('pairing.error.saveFailed'));
    }

    // Create persistent pairing if desktop fingerprint is available
    if (pairingInfo.desktop_fingerprint) {
      await ensurePersistentPairing(pairingInfo);
    }

    // URL에서 코드 경로 제거 (보안 + 깔끔한 URL)
    window.history.replaceState({}, document.title, '/');

    console.log('Short code pairing successful:', pairingInfo.device_id);
    return pairingInfo;
  } catch (e) {
    console.error('Short code processing failed:', e);
    throw e; // 에러를 던져서 앱에서 처리
  }
}

/**
 * Create a persistent pairing with the desktop (30-day lifetime)
 */
export async function createPersistentPairing(sessionInfo) {
  try {
    const phoneId = await getPhoneIdentifier();
    const phoneName = getPhoneName();

    const response = await fetch(`${API_BASE}/pairings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        desktopFingerprint: sessionInfo.desktop_fingerprint,
        phoneIdentifier: phoneId,
        desktopName: sessionInfo.desktop_name,
        phoneName: phoneName,
      }),
    });

    if (response.ok) {
      const pairing = await response.json();
      console.log('Persistent pairing created:', pairing.pairingId);

      // Store pairing for future use
      savePersistentPairing(pairing);
      return pairing;
    } else {
      console.warn('Failed to create persistent pairing:', response.status);
    }
  } catch (e) {
    console.warn('Persistent pairing creation failed:', e);
  }
  return null;
}

/**
 * Save a persistent pairing to localStorage
 */
function savePersistentPairing(pairing) {
  try {
    let pairings = JSON.parse(localStorage.getItem(PERSISTENT_PAIRINGS_KEY) || '[]');

    // Remove existing pairing with same desktop fingerprint
    pairings = pairings.filter(p => p.desktopFingerprint !== pairing.desktopFingerprint);

    // Add new pairing
    pairings.push({
      pairingId: pairing.pairingId,
      desktopFingerprint: pairing.desktopFingerprint,
      desktopName: pairing.desktopName,
      expiresAt: pairing.expiresAt,
      createdAt: new Date().toISOString(),
    });

    localStorage.setItem(PERSISTENT_PAIRINGS_KEY, JSON.stringify(pairings));
    console.log('Persistent pairing saved');
  } catch (e) {
    console.error('Failed to save persistent pairing:', e);
  }
}

/**
 * Get list of persistent pairings
 */
export function getPersistentPairings() {
  try {
    const pairings = JSON.parse(localStorage.getItem(PERSISTENT_PAIRINGS_KEY) || '[]');
    const now = new Date().toISOString();

    // Filter out expired pairings
    return pairings.filter(p => p.expiresAt > now);
  } catch (e) {
    console.error('Failed to get persistent pairings:', e);
    return [];
  }
}

/**
 * Find existing valid pairing for a desktop
 */
export function findPairingForDesktop(desktopFingerprint) {
  if (!desktopFingerprint) return null;
  const pairings = getPersistentPairings();
  return pairings.find(p => p.desktopFingerprint === desktopFingerprint) || null;
}

/**
 * Create pairing only if one doesn't exist
 */
export async function ensurePersistentPairing(sessionInfo) {
  if (!sessionInfo.desktop_fingerprint) return null;

  const existing = findPairingForDesktop(sessionInfo.desktop_fingerprint);
  if (existing) {
    console.log('Reusing existing pairing:', existing.pairingId);
    return existing;
  }

  return await createPersistentPairing(sessionInfo);
}

/**
 * URL 딥링크에서 페어링 정보 추출 및 처리 (레거시 Base64 방식)
 * @returns {Object|null} 페어링 정보 또는 null
 * @deprecated Greenfield: 새 short code 방식 사용 권장
 */
export function handleDeepLink() {
  try {
    const params = new URLSearchParams(window.location.search);
    const data = params.get('data');

    if (!data) return null;

    // URL 디코딩 후 Base64 디코딩
    const urlDecoded = decodeURIComponent(data);
    const decoded = atob(urlDecoded);
    const pairingData = JSON.parse(decoded);

    // 필수 필드 검증
    if (!pairingData.device_id || !pairingData.endpoint || !pairingData.credentials) {
      console.error('Invalid deep link data: missing required fields');
      return null;
    }

    if (!pairingData.credentials.apiKey) {
      console.error('Invalid deep link data: missing apiKey');
      return null;
    }

    // Confirm before overwriting if pending uploads exist
    if (!confirmPendingUploads()) {
      return null;
    }

    // 페어링 정보 저장
    const pairingInfo = {
      device_id: pairingData.device_id,
      endpoint: pairingData.endpoint,
      credentials: pairingData.credentials,
      paired_at: new Date().toISOString()
    };

    if (!savePairingInfo(pairingInfo)) {
      console.error('Failed to save pairing info from deep link');
      return null;
    }

    // URL에서 파라미터 제거 (보안)
    cleanUrlParams();

    console.log('Deep link pairing successful:', pairingInfo.device_id);
    return pairingInfo;
  } catch (e) {
    console.error('Deep link processing failed:', e);
    return null;
  }
}

/**
 * URL에서 페어링 파라미터 제거
 */
function cleanUrlParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete('data');
  // 다른 source 파라미터는 유지
  window.history.replaceState({}, document.title, url.pathname + (url.search || ''));
}

/**
 * 저장된 페어링 정보 가져오기
 */
export function getPairingInfo() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const info = JSON.parse(stored);
    // 필수 필드 검증
    if (!info.device_id || !info.endpoint || !info.credentials) {
      return null;
    }
    return info;
  } catch (e) {
    console.error('Failed to get pairing info:', e);
    return null;
  }
}

/**
 * Check if there are pending uploads and confirm before switching sessions.
 * Returns false if user cancels, true if ok to proceed (or no pending uploads).
 */
export function confirmPendingUploads() {
  const pendingCount = uploadQueue.getPendingCount();
  if (pendingCount > 0) {
    return window.confirm(t('pairing.confirmSwitch', { count: pendingCount }));
  }
  return true;
}

/**
 * 페어링 정보 저장
 * 새 세션 시작 시 이전 세션의 업로드 큐도 정리
 */
export function savePairingInfo(info) {
  try {
    // 새 세션 시작 전 이전 세션의 업로드 큐 정리
    // (이전 세션 파일은 새 세션에서 업로드 불가)
    uploadQueue.clear();

    localStorage.setItem(STORAGE_KEY, JSON.stringify(info));

    // Sentry 사용자 컨텍스트 설정
    setSentryUser(info.session_id, info.device_name);

    // 다른 탭에 세션 변경 알림
    notifySessionChanged();

    return true;
  } catch (e) {
    console.error('Failed to save pairing info:', e);
    return false;
  }
}

/**
 * 페어링 정보 삭제
 */
export function clearPairingInfo() {
  localStorage.removeItem(STORAGE_KEY);
  clearSentryUser();
}

/**
 * 페어링 여부 확인
 */
export function isPaired() {
  return getPairingInfo() !== null;
}

/**
 * QR 스캐너 시작
 * @param {string} elementId - QR 리더 DOM ID
 * @param {Function} onSuccess - 성공 콜백 (pairingInfo)
 * @param {Function} onError - 에러 콜백 (error)
 */
export async function startQRScanner(elementId, onSuccess, onError) {
  try {
    // 이전 스캐너 정리
    await stopQRScanner();

    qrScanner = new Html5Qrcode(elementId);

    const config = {
      fps: 15,
      qrbox: (viewfinderWidth, viewfinderHeight) => {
        const size = Math.min(viewfinderWidth, viewfinderHeight) * 0.7;
        return { width: size, height: size };
      },
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
    };

    await qrScanner.start(
      { facingMode: 'environment' },
      config,
      (decodedText) => {
        handleQRCode(decodedText, onSuccess, onError);
      },
      (errorMessage) => {
        // QR 인식 실패는 무시 (계속 스캔)
      }
    );
  } catch (e) {
    console.error('Failed to start QR scanner:', e);
    onError(new Error(t('pairing.error.cameraNotAllowed')));
  }
}

/**
 * QR 스캐너 중지
 */
export async function stopQRScanner() {
  if (qrScanner) {
    try {
      const state = qrScanner.getState();
      if (state === Html5QrcodeScannerState.SCANNING) {
        await qrScanner.stop();
      }
    } catch (e) {
      // 무시
    }
    qrScanner = null;
  }
}

/**
 * QR 코드 처리
 * 신규: go.scantodesk.app/s/{code} 형식 지원
 * 레거시: Base64 JSON 형식 지원 (하위 호환)
 */
async function handleQRCode(decodedText, onSuccess, onError) {
  try {
    // QR 스캐너 중지
    await stopQRScanner();

    // URL 형식 확인
    if (decodedText.startsWith('http://') || decodedText.startsWith('https://')) {
      try {
        const url = new URL(decodedText);

        // 신규 방식: /s/{code} 경로 확인
        const codeMatch = url.pathname.match(/^\/s\/([A-HJ-NP-Z2-9]{7})$/i);
        if (codeMatch) {
          const code = codeMatch[1].toUpperCase();
          console.log('QR short code detected:', code);

          // Extract desktop name from URL parameter
          const desktopName = extractDesktopName(decodedText);
          if (desktopName) {
            console.log('Desktop name from QR:', desktopName);
          }

          // API 호출하여 세션 정보 조회
          const response = await fetch(`${API_BASE}/s/${code}`);
          const data = await response.json();

          if (!data.valid) {
            if (data.error === 'code_expired') {
              throw new Error(t('pairing.error.codeExpired'));
            } else if (data.error === 'code_not_found') {
              throw new Error(t('pairing.error.codeNotFound'));
            }
            throw new Error(t('pairing.error.connectionFailed'));
          }

          // Confirm before overwriting if pending uploads exist
          if (!confirmPendingUploads()) {
            startQRScanner('qr-reader', onSuccess, onError);
            return;
          }

          const pairingInfo = {
            device_id: data.sessionId,
            endpoint: data.endpoint,
            credentials: { apiKey: data.apiKey },
            desktop_name: desktopName || data.deviceName || null,
            desktop_fingerprint: data.fingerprint || null,
            paired_at: new Date().toISOString()
          };

          if (!savePairingInfo(pairingInfo)) {
            throw new Error(t('pairing.error.saveFailed'));
          }

          // Create persistent pairing if desktop fingerprint is available
          if (pairingInfo.desktop_fingerprint) {
            await ensurePersistentPairing(pairingInfo);
          }

          onSuccess(pairingInfo);
          return;
        }

        // 레거시 방식: ?data= 파라미터에서 Base64 JSON 추출
        const encoded = url.searchParams.get('data');
        if (encoded) {
          const decoded = atob(decodeURIComponent(encoded));
          const data = JSON.parse(decoded);

          if (!data.device_id || !data.endpoint || !data.credentials?.apiKey) {
            throw new Error(t('pairing.error.invalidQr'));
          }

          // Confirm before overwriting if pending uploads exist
          if (!confirmPendingUploads()) {
            startQRScanner('qr-reader', onSuccess, onError);
            return;
          }

          const pairingInfo = {
            device_id: data.device_id,
            endpoint: data.endpoint,
            credentials: data.credentials,
            paired_at: new Date().toISOString()
          };

          if (!savePairingInfo(pairingInfo)) {
            throw new Error(t('pairing.error.saveFailed'));
          }

          onSuccess(pairingInfo);
          return;
        }

        throw new Error(t('pairing.error.noData'));
      } catch (urlErr) {
        // Re-throw localized errors
        if (urlErr.message && !urlErr.message.startsWith('pairing.')) {
          throw urlErr;
        }
        throw new Error(t('pairing.error.invalidQr'));
      }
    }

    // 레거시: JSON 직접 파싱 (하위 호환)
    const data = JSON.parse(decodedText);

    if (!data.device_id || !data.endpoint || !data.credentials?.apiKey) {
      throw new Error(t('pairing.error.invalidQr'));
    }

    // Confirm before overwriting if pending uploads exist
    if (!confirmPendingUploads()) {
      startQRScanner('qr-reader', onSuccess, onError);
      return;
    }

    const pairingInfo = {
      device_id: data.device_id,
      endpoint: data.endpoint,
      credentials: data.credentials,
      paired_at: new Date().toISOString()
    };

    if (!savePairingInfo(pairingInfo)) {
      throw new Error(t('pairing.error.saveFailed'));
    }

    onSuccess(pairingInfo);
  } catch (e) {
    console.error('QR code processing failed:', e);
    onError(e);

    // 에러 후 스캐너 재시작
    setTimeout(() => {
      startQRScanner('qr-reader', onSuccess, onError);
    }, 2000);
  }
}

/**
 * Check if desktop is online via pairing status API
 * @param {string} pairingId - The pairing ID to check
 * @returns {Promise<{online: boolean, desktopName: string|null, session: Object|null}>}
 */
export async function checkDesktopStatus(pairingId) {
  try {
    const phoneId = await getPhoneIdentifier();
    const response = await fetch(`${API_BASE}/pairings/${pairingId}/status`, {
      headers: {
        'X-Phone-Identifier': phoneId,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return {
        online: data.desktopOnline || false,
        desktopName: data.desktopName || null,
        lastHeartbeat: data.lastHeartbeat || null,
        session: data.session || null,
      };
    }

    // Pairing not found or expired
    if (response.status === 404 || response.status === 410) {
      return { online: false, desktopName: null, expired: true, session: null };
    }

    return { online: false, desktopName: null, session: null };
  } catch (e) {
    console.error('Failed to check desktop status:', e);
    return { online: false, desktopName: null, session: null };
  }
}

/**
 * Get the desktop name from current session/pairing
 */
export function getDesktopName() {
  const pairing = getPairingInfo();
  return pairing?.desktop_name || null;
}

// ============ Link Sharing Flow ============

const LINK_INFO_KEY = 'scantodesk_link';

/**
 * Handle link sharing URL (/l/{code})
 * @returns {Promise<Object|null>} Link info or null
 */
export async function handleLinkUrl() {
  try {
    const path = window.location.pathname;
    const codeMatch = path.match(/^\/l\/([A-HJ-NP-Z2-9]{7})$/i);

    if (!codeMatch) return null;

    const code = codeMatch[1].toUpperCase();
    console.log('Link code detected:', code);

    // API 호출하여 링크 정보 조회
    const response = await fetch(`${API_BASE}/l/${code}`);
    const data = await response.json();

    if (!data.valid) {
      console.error('Link invalid:', data.error);
      if (data.error === 'link_expired') {
        throw new Error(t('link.error.expired'));
      } else if (data.error === 'link_not_found') {
        throw new Error(t('link.error.notFound'));
      }
      throw new Error(t('link.error.failed'));
    }

    // Link info 저장
    const linkInfo = {
      link_id: data.linkId,
      recipient_name: data.recipientName,
      desktop_online: data.desktopOnline,
      expires_at: data.expiresAt,
      is_link_mode: true, // 링크 모드 플래그
    };

    saveLinkInfo(linkInfo);

    // URL에서 코드 경로 제거
    window.history.replaceState({}, document.title, '/');

    console.log('Link info loaded:', linkInfo.link_id);
    return linkInfo;
  } catch (e) {
    console.error('Link URL processing failed:', e);
    throw e;
  }
}

/**
 * Save link info to localStorage
 */
function saveLinkInfo(info) {
  try {
    localStorage.setItem(LINK_INFO_KEY, JSON.stringify(info));
    return true;
  } catch (e) {
    console.error('Failed to save link info:', e);
    return false;
  }
}

/**
 * Get saved link info
 */
export function getLinkInfo() {
  try {
    const stored = localStorage.getItem(LINK_INFO_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to get link info:', e);
    return null;
  }
}

/**
 * Clear link info
 */
export function clearLinkInfo() {
  localStorage.removeItem(LINK_INFO_KEY);
}

/**
 * Check if current session is link mode (not pairing mode)
 */
export function isLinkMode() {
  const linkInfo = getLinkInfo();
  return linkInfo?.is_link_mode === true;
}
